import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, Trash2, Eye, EyeOff } from "lucide-react";

export const ReviewsTab = () => {
  const [reviews, setReviews] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/reviews").then((res) => setReviews(res.data)).catch(() => toast.error("Could not load reviews"));
  }, []);

  useEffect(load, [load]);

  const toggle = async (r) => {
    try {
      await api.patch(`/admin/reviews/${r.id}`, { visible: !r.visible });
      toast.success(r.visible ? "Review hidden from website" : "Review now visible on website");
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/reviews/${id}`);
      toast.success("Review deleted");
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const publicCount = reviews.filter((r) => r.visible).length;
  const privateCount = reviews.length - publicCount;

  return (
    <div data-testid="reviews-tab">
      <div className="mb-6">
        <h2 className="font-serif-display text-2xl font-semibold text-[#0A192F]">Guest Reviews ({reviews.length})</h2>
        <p className="text-[#4A5568] text-sm mt-1.5">
          <span className="text-emerald-700 font-semibold">{publicCount} shown on website</span> ·{" "}
          <span className="text-[#8a6c2f] font-semibold">{privateCount} private</span> — only 4–5 star reviews appear on the main page automatically.
        </p>
      </div>

      {reviews.length === 0 && (
        <p className="text-[#4A5568] text-sm bg-white/50 rounded-2xl p-10 text-center" data-testid="reviews-empty">No reviews yet. Guest reviews appear here as they come in.</p>
      )}

      <div className="space-y-4">
        {reviews.map((r) => (
          <div key={r.id} className={`bg-[#FAF8F5] border rounded-2xl p-6 ${r.visible ? "border-[#C5A059]/40" : "border-[#0D1B2A]/10 opacity-75"}`} data-testid={`review-admin-card-${r.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="font-serif-display text-lg font-semibold text-[#0A192F]">{r.name}</h3>
                  <span className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} size={13} className={n <= r.rating ? "fill-[#C5A059] text-[#C5A059]" : "text-[#0D1B2A]/20"} />
                    ))}
                  </span>
                  <span className={`text-[10px] uppercase tracking-widest font-semibold border rounded-full px-3 py-1 ${r.visible ? "bg-emerald-500/10 text-emerald-700 border-emerald-300" : "bg-[#C5A059]/15 text-[#8a6c2f] border-[#C5A059]/40"}`} data-testid={`review-status-${r.id}`}>
                    {r.visible ? "On website" : "Private"}
                  </span>
                </div>
                <p className="text-[#4A5568] text-sm mt-2 leading-relaxed">“{r.text}”</p>
                <p className="text-[#0D1B2A]/40 text-xs mt-2">{new Date(r.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => toggle(r)}
                  className="flex items-center gap-2 text-xs font-semibold rounded-full px-4 py-2.5 border border-[#0D1B2A]/20 hover:border-[#C5A059] text-[#0D1B2A]/70 transition-all"
                  data-testid={`review-toggle-${r.id}`}
                >
                  {r.visible ? <><EyeOff size={13} /> Hide</> : <><Eye size={13} /> Show</>}
                </button>
                <button onClick={() => remove(r.id)} className="text-red-400 hover:text-red-600 p-2 transition-colors" data-testid={`review-delete-${r.id}`} aria-label="Delete review">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
